/**
 * Fika AJAX Cart Drawer — Vanilla JS, zero deps.
 *
 * FIX: el listener de [data-fika-open] se registra SIEMPRE en document,
 * ANTES de cualquier chequeo de null. Así el botón del header funciona
 * incluso si la sección tiene problemas de render en un deployment parcial.
 */

const SECTION_ID       = 'fika-ajax-cart-drawer';
const CART_UPDATE_EVENT = 'cart:update';

class FikaCartDrawer {
  /** @type {HTMLElement|null} */  #root    = null;
  /** @type {HTMLElement|null} */  #panel   = null;
  /** @type {HTMLElement|null} */  #body    = null;
  /** @type {HTMLElement|null} */  #footer  = null;
  /** @type {HTMLElement|null} */  #loading = null;
  /** @type {HTMLElement|null} */  #count   = null;
  /** @type {boolean} */           #isOpen  = false;
  /** @type {boolean} */           #isFetching = false;
  /** @type {AbortController|null} */ #abortCtrl = null;
  /** @type {HTMLElement|null} */  #returnFocus = null;
  /** @type {number} */            #savedScrollY = 0;

  constructor() {
    // ── 1. El listener del botón del carrito se registra SIEMPRE ──
    // No depende de que la sección esté en el DOM.
    this.#bindCartIconTrigger();

    // ── 2. Localizar el DOM del drawer ──
    this.#root = document.querySelector('[data-fika-drawer]');

    if (!this.#root) {
      console.warn('[FikaCart] ⚠️  La sección fika-ajax-cart-drawer no está en el DOM. Revisa que esté publicada en el tema activo.');
      // El trigger sigue escuchando — cuando el drawer exista, funcionará.
      return;
    }

    this.#panel   = this.#root.querySelector('.fcd-panel');
    this.#body    = this.#root.querySelector('[data-fika-body]');
    this.#footer  = this.#root.querySelector('[data-fika-footer]');
    this.#loading = this.#root.querySelector('[data-fika-loading]');
    this.#count   = this.#root.querySelector('[data-fika-count]');

    // ── 3. Resto de listeners (cerrar, qty, cart:update, etc.) ──
    this.#bindEvents();
  }

  // ─────────────────────────────────────────────────────────
  // PUBLIC
  // ─────────────────────────────────────────────────────────

  open() {
    if (!this.#root || this.#isOpen) return;
    this.#isOpen = true;
    this.#returnFocus = /** @type {HTMLElement} */ (document.activeElement);
    this.#savedScrollY = window.scrollY;

    this.#root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('fcd-scroll-locked');
    document.body.style.setProperty('--fcd-scroll-offset', `${this.#savedScrollY}px`);

    requestAnimationFrame(() => {
      this.#panel?.focus({ preventScroll: true });
    });
  }

  close() {
    if (!this.#root || !this.#isOpen) return;
    this.#isOpen = false;

    this.#root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fcd-scroll-locked');
    document.body.style.removeProperty('--fcd-scroll-offset');
    window.scrollTo({ top: this.#savedScrollY, behavior: 'instant' });

    this.#returnFocus?.focus({ preventScroll: true });
    this.#returnFocus = null;
  }

  // ─────────────────────────────────────────────────────────
  // PRIVATE — TRIGGERS
  // ─────────────────────────────────────────────────────────

  /**
   * Registra el listener del botón del carrito en el header.
   * Se llama ANTES de cualquier null-check para que funcione siempre.
   */
  #bindCartIconTrigger() {
    document.addEventListener('click', (e) => {
      const trigger = /** @type {HTMLElement} */ (e.target).closest('[data-fika-open]');
      if (!trigger) return;

      // Si el drawer aún no está en DOM, intentamos re-localizarlo
      if (!this.#root) {
        this.#root   = document.querySelector('[data-fika-drawer]');
        this.#panel  = this.#root?.querySelector('.fcd-panel')   ?? null;
        this.#body   = this.#root?.querySelector('[data-fika-body]')   ?? null;
        this.#footer = this.#root?.querySelector('[data-fika-footer]') ?? null;
        this.#loading = this.#root?.querySelector('[data-fika-loading]') ?? null;
        this.#count  = this.#root?.querySelector('[data-fika-count]')  ?? null;
        if (this.#root) this.#bindEvents();
      }

      if (!this.#root) {
        console.error('[FikaCart] ❌ Drawer no encontrado — sube la sección al tema activo.');
        return;
      }

      this.open();
      this.#setLoading(true);
      this.#refreshSection().finally(() => this.#setLoading(false));
    });
  }

  // ─────────────────────────────────────────────────────────
  // PRIVATE — EVENTOS
  // ─────────────────────────────────────────────────────────

  #bindEvents() {
    // Cerrar (overlay, botón X, "keep shopping")
    this.#root.addEventListener('click', (e) => {
      if (/** @type {HTMLElement} */ (e.target).closest('[data-fika-close]')) {
        this.close();
      }
    });

    // Escape + trap focus
    document.addEventListener('keydown', (e) => {
      if (!this.#isOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key === 'Tab')    this.#trapFocus(e);
    });

    // Cambio de cantidad / remove
    this.#root.addEventListener('click', (e) => {
      const btn = /** @type {HTMLElement} */ (e.target).closest('[data-fika-qty-change]');
      if (!btn) return;
      const key = btn.dataset.key;
      const qty = parseInt(btn.dataset.qty ?? '0', 10);
      if (key !== undefined) this.#changeLineItem(key, qty);
    });

    // cart:update — evento nativo del tema (product-form.js, quick-add.js…)
    document.addEventListener(CART_UPDATE_EVENT, (e) => {
      const detail = /** @type {any} */ (e).detail ?? {};
      if (detail.data?.didError) return;
      this.#onCartUpdate(detail.resource?.item_count);
    });

    // Fallback: formularios ATC no manejados por product-form-component
    document.addEventListener('submit', (e) => {
      const form = /** @type {HTMLFormElement} */ (e.target);
      if (!form.matches('form[action*="/cart/add"]')) return;
      if (form.closest('product-form-component'))    return;
      // Skip forms that manage their own ATC submission (e.g. fika-buy-zone)
      if (form.classList.contains('fbz-cta-card__form')) return;
      e.preventDefault();
      this.#submitAtcForm(form);
    }, { capture: true });
  }

  // ─────────────────────────────────────────────────────────
  // PRIVATE — CART OPERATIONS
  // ─────────────────────────────────────────────────────────

  /** @param {number|undefined} itemCount */
  async #onCartUpdate(itemCount) {
    this.open();
    this.#setLoading(true);
    try {
      await this.#refreshSection();
      if (itemCount !== undefined) this.#syncBubble(itemCount);
    } finally {
      this.#setLoading(false);
    }
  }

  /** @param {HTMLFormElement} form */
  async #submitAtcForm(form) {
    // Pre-check: ensure adding 1 unit won't exceed the 3-unit limit
    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (variantId) {
      try {
        const currentCart = await this.#fetchCart();
        const cartItem = /** @type {any[]} */ (currentCart.items ?? [])
          .find((item) => item.variant_id.toString() === variantId.toString());
        if (cartItem && cartItem.quantity >= 3) {
          this.#showMaxToast();
          return;
        }
      } catch (_) {
        // Cart fetch failed — proceed and let Shopify enforce its own limits
      }
    }

    this.#setLoading(true);
    const btn = form.querySelector('[type="submit"]');
    btn?.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.warn('[FikaCart] ATC error:', err.description ?? res.statusText);
        return;
      }

      const cart = await this.#fetchCart();
      document.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: { resource: { item_count: cart.item_count }, data: {} },
      }));

      this.open();
      await this.#refreshSection();
      this.#syncBubble(cart.item_count);
    } catch (err) {
      console.error('[FikaCart] Submit error:', err);
    } finally {
      this.#setLoading(false);
      btn?.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} key
   * @param {number} qty
   */
  async #changeLineItem(key, qty) {
    // Block increases that would exceed the 3-unit limit
    if (qty > 3) {
      this.#showMaxToast();
      return;
    }

    if (this.#isFetching) return;
    const item = this.#root?.querySelector(`.fcd-item[data-key="${CSS.escape(key)}"]`);
    item?.classList.add('fcd-item--loading');
    this.#setLoading(true);

    try {
      const res = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ id: key, quantity: qty }),
      });
      if (!res.ok) throw new Error(res.statusText);

      const cart = await res.json();
      this.#syncBubble(cart.item_count);
      await this.#refreshSection();

      document.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: { resource: { item_count: cart.item_count }, data: { source: 'fika-drawer' } },
      }));
    } catch (err) {
      console.error('[FikaCart] Change error:', err);
      item?.classList.remove('fcd-item--loading');
    } finally {
      this.#setLoading(false);
    }
  }

  // ─────────────────────────────────────────────────────────
  // PRIVATE — SECTION RENDERING API
  // ─────────────────────────────────────────────────────────

  async #refreshSection() {
    this.#abortCtrl?.abort();
    this.#abortCtrl = new AbortController();

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', SECTION_ID);
      url.searchParams.delete('page');

      const res = await fetch(url.toString(), {
        signal:  this.#abortCtrl.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!res.ok) throw new Error(`SRA ${res.status}`);
      this.#injectSectionHTML(await res.text());
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[FikaCart] Refresh error:', err);
    }
  }

  /** @param {string} html */
  #injectSectionHTML(html) {
    const doc     = new DOMParser().parseFromString(html, 'text/html');
    const newRoot = doc.querySelector('[data-fika-drawer]');
    if (!newRoot) return;

    // NOTE: private class fields cannot be accessed via bracket notation (this['#body'])
    // so we reference them directly here.
    const newBody = newRoot.querySelector('[data-fika-body]');
    if (newBody && this.#body) this.#body.innerHTML = newBody.innerHTML;

    const newFooter = newRoot.querySelector('[data-fika-footer]');
    if (newFooter && this.#footer) this.#footer.innerHTML = newFooter.innerHTML;

    // Actualizar contador
    const newCount = newRoot.querySelector('[data-fika-count]');
    if (newCount && this.#count) {
      const n = newCount.textContent?.trim() ?? '0';
      this.#count.textContent = n;
      this.#count.classList.toggle('fcd-count--zero', n === '0');
    }

    // Mostrar/ocultar footer
    const isEmpty = !newRoot.querySelector('.fcd-items');
    this.#footer?.classList.toggle('fcd-footer--hidden', isEmpty);
  }

  // ─────────────────────────────────────────────────────────
  // PRIVATE — HEADER BUBBLE SYNC
  // ─────────────────────────────────────────────────────────

  /** @param {number} count */
  #syncBubble(count) {
    document.querySelectorAll('[ref="cartBubbleCount"], [data-testid="cart-bubble"]').forEach((el) => {
      el.textContent = count > 99 ? '99+' : String(count);
    });
    document.querySelectorAll('[ref="cartBubble"], .cart-bubble').forEach((el) => {
      el.classList.toggle('visually-hidden', count === 0);
    });
    document.querySelectorAll('[ref="cartBubble"]').forEach((el) => {
      count <= 99 ? el.setAttribute('data-maintain-ratio', '') : el.removeAttribute('data-maintain-ratio');
    });
    // Badge del morphing header
    const morphBadge = document.getElementById('morphCartBadge');
    if (morphBadge) {
      morphBadge.textContent    = count > 0 ? String(count) : '';
      morphBadge.dataset.count  = String(count);
    }
  }

  // ─────────────────────────────────────────────────────────
  // PRIVATE — HELPERS
  // ─────────────────────────────────────────────────────────

  /** @param {boolean} active */
  #setLoading(active) {
    this.#isFetching = active;
    this.#loading?.setAttribute('aria-hidden', active ? 'false' : 'true');
  }

  /** @returns {Promise<{item_count:number, items: any[]}>} */
  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    return res.json();
  }

  /** Shows a temporary toast informing the user of the 3-unit limit */
  #showMaxToast() {
    const existing = document.querySelector('.fika-max-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'fika-max-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.textContent = 'Máximo 3 piezas por producto';
    Object.assign(toast.style, {
      position:    'fixed',
      bottom:      '90px',
      left:        '50%',
      transform:   'translateX(-50%)',
      background:  '#000000',
      color:       '#ffffff',
      padding:     '10px 22px',
      borderRadius:'8px',
      fontSize:    '13px',
      fontWeight:  '600',
      letterSpacing:'0.02em',
      zIndex:      '200000',
      pointerEvents:'none',
      opacity:     '1',
      transition:  'opacity 0.3s ease',
      fontFamily:  'var(--font-body-family, Inter, system-ui, sans-serif)',
    });
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  /** @param {KeyboardEvent} e */
  #trapFocus(e) {
    const focusables = /** @type {NodeListOf<HTMLElement>} */ (
      this.#panel?.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last  = focusables[focusables.length - 1];
    if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus();  } }
    else            { if (document.activeElement === last)  { e.preventDefault(); first.focus(); } }
  }
}

// ─────────────────────────────────────────────────────────
// BOOT
// ─────────────────────────────────────────────────────────
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FikaCartDrawer());
} else {
  new FikaCartDrawer();
}
